////////////////////////////////////////////////////////////////////////////////
//
// Store TTN module
//
// Unlike the list-oriented modules (app, device, dispenser), TTN state is keyed
// by entity name. That is deliberate: one entity has at most one TTN
// integration, and a superadmin can view several entities in the same session.
//
// State shape:
//   byEntity: {
//     "Infrafon_dev": { ...integration document from the backend... },
//     "acme":         { ... }
//   }
//
////////////////////////////////////////////////////////////////////////////////

import Vue from 'vue';

import {Action} from "@/store/actions";
import {Mutation} from "@/store/mutations";
import ttnApi from "@/services/api/ttn";

////////////////////////////////////////////////////////////////////////////////

const state = {
    // { "<entity>": { ...integration... } }
    byEntity: {},
};

////////////////////////////////////////////////////////////////////////////////

const getters = {
    /**
     * Return the stored integration for an entity, or null if none.
     * Usage: this.$store.getters.ttnIntegration('Infrafon_dev')
     */
    ttnIntegration: state => (entity) => {
        if (!entity) return null;
        return state.byEntity[entity] || null;
    },
};

////////////////////////////////////////////////////////////////////////////////

const actions = {
    /**
     * Load the TTN integration for an entity.
     * A 404 is the expected "not configured yet" case: it clears any stale entry
     * and resolves with null, without showing an error toast.
     * Any other error (network, 401, 500) is toasted via notifyApiError and rejected.
     */
    async [Action.ttn.load](context, entity) {
        try {
            const result = await ttnApi.get(entity);
            context.commit(Mutation.ttn.set, {entity, data: result.data});
            return result.data;
        } catch (error) {
            const msg = (error && error.message) || '';
            if (/no ttn integration/i.test(msg)) {
                context.commit(Mutation.ttn.clear, entity);
                return null;
            }
            context.commit(Mutation.notifyApiError, error);
            return Promise.reject(error);
        }
    },

    /**
     * Create or update the integration.
     * On success, commits the returned document and resolves with it. The modal
     * is responsible for choosing the success/warning toast based on `status`.
     */
    async [Action.ttn.save](context, {entity, data}) {
        try {
            const result = await ttnApi.save(entity, data);
            context.commit(Mutation.ttn.set, {entity, data: result.data});
            return result.data;
        } catch (error) {
            context.commit(Mutation.notifyApiError, error);
            return Promise.reject(error);
        }
    },

    /**
     * Re-run the connection test. Same contract as `save`.
     */
    async [Action.ttn.test](context, entity) {
        try {
            const result = await ttnApi.test(entity);
            context.commit(Mutation.ttn.set, {entity, data: result.data});
            return result.data;
        } catch (error) {
            context.commit(Mutation.notifyApiError, error);
            return Promise.reject(error);
        }
    },

    /**
     * Remove the integration and clear the cached entry.
     */
    async [Action.ttn.remove](context, entity) {
        try {
            await ttnApi.remove(entity);
            context.commit(Mutation.ttn.clear, entity);
        } catch (error) {
            context.commit(Mutation.notifyApiError, error);
            return Promise.reject(error);
        }
    },
        /**
     * Provision or re-provision a LoRa device.
     * On 200, resolves with the device.
     * On 502, rejects with {message, provision_detail, ...} — the caller
     * decides how to display it (usually a warning: device created, TTN part failed).
     * Deliberately does not toast; the component owns the messaging.
     */
    async [Action.ttn.deviceProvision](context, { entity, name }) {
        const result = await ttnApi.provisionDevice(entity, name);
        return result.data;
    },

    /**
     * Detach a LoRa device from TTN.
     * On 200, resolves with the updated device (transport now 'wifi').
     * On 502, rejects with {message, provision_detail}.
     */
    async [Action.ttn.deviceDetach](context, { entity, name }) {
        const result = await ttnApi.detachDevice(entity, name);
        return result.data;
    },
};

////////////////////////////////////////////////////////////////////////////////

const mutations = {
    [Mutation.ttn.set](state, {entity, data}) {
        // Vue.set so that adding a new key to the map triggers reactivity.
        Vue.set(state.byEntity, entity, data);
    },
    [Mutation.ttn.clear](state, entity) {
        Vue.delete(state.byEntity, entity);
    },
};

////////////////////////////////////////////////////////////////////////////////

const ttn = {
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations,
};

export default ttn;